import { action } from 'mobx';
import { SessionStore } from './session.store';
import { AggregateFunction, AgtAnalyticsStore, SelectColumn } from './agt.analytics.store';

export interface IAgenturAmisNowModel {
	vtnrId: number;
	bm: number;
	// Mapping from DB columns to UI property names:
	// abs_tkh -> telefonieNachrichVonKvmHalle (a)
	// akuma -> aufgabeAusAkuma (b)
	// azamservices -> kundennachrichtVertriebschance (c)
	// dssk -> kuendigungsinformation (d)
	// amisnow_postkorb -> aufgabeAusAmisnowPostkorb (f)
	// gomservice -> freigabeAngebot (g)
	// leadmachine -> kundennachrichtServiceanliegen (h)
	// meineallianz -> meineAllianzNachricht (i)
	// tkh_interessent -> telefonieNachrichtVonKvmHalleInteressent (j)
	// manual -> eigeneAufgaben (k)
	// meingeld -> kundennachrichtVertriebschanceWiederanlage (l)
	// summe -> summe (m)
	// davon_read -> davonRead (n)
	// quote_read -> quoteRead (o)
}

export class AgenturAmisNowModel implements IAgenturAmisNowModel {
	vtnrId: number;
	bm: number;

	// Legacy compatibility properties
	agtId: number;
	j: any;

	constructor(data: any) {
		this.vtnrId = data.aid || 0;
		this.bm = data.bm ? parseInt(data.bm) : 0;

		// Legacy compatibility - alias properties
		this.agtId = this.vtnrId;

		// Legacy j property for backwards compatibility with KvAgtMsLiteModel structure
		// Map database columns to the short aliases expected by AmisNowModel
		this.j = {
			a: data.a || 0, // abs_tkh -> telefonieNachrichVonKvmHalle
			b: data.b || 0, // akuma -> aufgabeAusAkuma
			c: data.c || 0, // azamservices -> kundennachrichtVertriebschance
			d: data.d || 0, // dssk -> kuendigungsinformation
			f: data.f || 0, // amisnow_postkorb -> aufgabeAusAmisnowPostkorb
			g: data.g || 0, // gomservice -> freigabeAngebot
			h: data.h || 0, // leadmachine -> kundennachrichtServiceanliegen
			i: data.i || 0, // meineallianz -> meineAllianzNachricht
			j: data.j || 0, // tkh_interessent -> telefonieNachrichtVonKvmHalleInteressent
			k: data.k || 0, // manual -> eigeneAufgaben
			l: data.l || 0, // meingeld -> kundennachrichtVertriebschanceWiederanlage
			m: data.m || 0, // summe -> summe
			n: data.n || 0, // davon_read -> davonRead
			//o: data.o || 0,  // quote_read -> quoteRead
		};
		if (this.j.m > 0) {
			this.j.o = this.j.n / this.j.m; // quote_read = davonRead / summe
		}
	}
}

export class AgtAmisNowStore extends AgtAnalyticsStore {
	constructor(session: SessionStore) {
		super(session, {
			tableName: 'agentur_amisnowaufgaben',
			defaultColumns: [
				{ column: 'vtnr_id', aggregateFunction: AggregateFunction.NONE, alias: 'aid', groupBy: true },
				// { column: 'bm', aggregateFunction: AggregateFunction.NONE },
				{ column: 'abs_tkh', aggregateFunction: AggregateFunction.SUM, alias: 'a' },
				{ column: 'akuma', aggregateFunction: AggregateFunction.SUM, alias: 'b' },
				{ column: 'azamservices', aggregateFunction: AggregateFunction.SUM, alias: 'c' },
				{ column: 'dssk', aggregateFunction: AggregateFunction.SUM, alias: 'd' },
				{ column: 'amisnow_postkorb', aggregateFunction: AggregateFunction.SUM, alias: 'f' },
				{ column: 'gomservice', aggregateFunction: AggregateFunction.SUM, alias: 'g' },
				{ column: 'leadmachine', aggregateFunction: AggregateFunction.SUM, alias: 'h' },
				{ column: 'meineallianz', aggregateFunction: AggregateFunction.SUM, alias: 'i' },
				{ column: 'tkh_interessent', aggregateFunction: AggregateFunction.SUM, alias: 'j' },
				{ column: 'manual', aggregateFunction: AggregateFunction.SUM, alias: 'k' },
				{ column: 'meingeld', aggregateFunction: AggregateFunction.SUM, alias: 'l' },
				{ column: 'summe', aggregateFunction: AggregateFunction.SUM, alias: 'm' },
				{ column: 'davon_read', aggregateFunction: AggregateFunction.SUM, alias: 'n' },
				// { column: 'quote_read', aggregateFunction: AggregateFunction.SUM, alias: 'o' },
			],
		});
		this.queryName = '?amisnowaufgaben';
	}

	async getDistinctBMs(): Promise<number[]> {
		const selectColumns: SelectColumn[] = [{ column: 'bm', aggregateFunction: AggregateFunction.NONE, distinct: true }];
		const results = await this.query(selectColumns);
		return results.map((data) => parseInt(data.bm)).sort((a, b) => a - b);
	}

	@action
	async aggregateByBm(minBm?: number, maxBm?: number): Promise<AgenturAmisNowModel[]> {
		let whereClause = '';
		if (minBm && maxBm) {
			whereClause = `bm BETWEEN ${minBm} AND ${maxBm}`;
		} else if (minBm) {
			whereClause = `bm >= ${minBm}`;
		} else if (maxBm) {
			whereClause = `kw <= ${maxBm}`;
		}
		const results = await this.query(this.config.defaultColumns, whereClause);
		return results.map((data) => new AgenturAmisNowModel(data));
	}
}
